import template from './ko-field-input-multiselect-list.html';

class MultiSelectListInputComponentVM {
	constructor(params) {
		this.params = params;

		this.name = ko.observable(params.name);
		this.label = ko.observable(params.label);
		this.$input = ko_helper.safe_observable(params.$input);
		this.help_text = ko.observable(params.help_text);

		this.options = ko.observableArray([]);
		this.optionsText = ko_helper.safe_observable(params.options_text || params.list_label_field, null);
		this.optionsValue = ko_helper.safe_observable(params.options_value || params.list_value_field, null);
		this.optionsCaption = ko_helper.safe_observable(params.caption, 'Select...');
		this.selectedOptions = ko.observableArray([]);
		this._updating = false;

		// Subscribe to selectedOptions changes
		this.selectedOptions.subscribe(newVal => {
			if (this._updating) return;
			this._updating = true;

			try
			{
				if (!newVal || !Array.isArray(newVal))
				{
					this.$input([]);
					return;
				}

				// Remove duplicates, null/undefined values, and empty strings
				const uniqueValues = Array.from(new Set(newVal.filter(v =>
					v !== null &&
					v !== undefined &&
					v !== '' &&
					(typeof v === 'string' ? v.trim() !== '' : true)
				)));

				// For manual items or when optionsValue
				if (this.optionsValue() || typeof this.options()[0] === 'string' || typeof this.options()[0] === 'number')
				{
					const values = uniqueValues.map(val => {
						if (this.optionsValue() && typeof val === 'object')
							return val[this.optionsValue()];

						return val;
					});
					this.$input(values);
				}
				else
				{
					// For cache items without optionsValue, save the full objects
					const options = this.options();
					const savedValues = uniqueValues.map(val => {
						const matchingOption = options.find(opt => {
							if (this.optionsValue())
								return opt[this.optionsValue()] === val[this.optionsValue()];

							return Object.keys(opt).every(key => opt[key] === val[key]);
						});
						return matchingOption || val;
					});
					this.$input(savedValues);
				}
			} finally
			{
				this._updating = false;
			}
		});

		// Subscribe to $input changes
		this.$input.subscribe(newVal => {
			if (this._updating) return;
			this._updating = true;

			try
			{
				if (!newVal || !Array.isArray(newVal))
				{
					this.selectedOptions([]);
					return;
				}

				// Remove duplicates, null/undefined values, and empty strings
				const uniqueValues = Array.from(new Set(newVal.filter(v =>
					v !== null &&
					v !== undefined &&
					v !== '' &&
					(typeof v === 'string' ? v.trim() !== '' : true)
				)));

				if (uniqueValues.length === 0)
				{
					this.selectedOptions([]);
					return;
				}

				// If options loaded, find the matching options
				const options = this.options();
				if (options.length > 0)
				{
					const matchingOptions = uniqueValues.map(val => {
						if (this.optionsValue())
						{
							return options.find(opt => {
								const optValue = opt[this.optionsValue()];
								const valValue = typeof val === 'object' ? val[this.optionsValue()] : val;
								return optValue === valValue;
							});
						}
						// For cache items without optionsValue, match by all properties
						if (typeof val === 'object')
						{
							return options.find(opt =>
								Object.keys(opt).every(key => opt[key] === val[key])
							);
						}
						return options.find(opt => opt === val);
					}).filter(Boolean);

					if (matchingOptions.length > 0)
					{
						this.selectedOptions(matchingOptions);
						return;
					}
				}

				this.selectedOptions(uniqueValues);
			} finally
			{
				this._updating = false;
			}
		});

		// Initialize value from $input
		const initialValue = this.$input();
		if (initialValue !== undefined && initialValue !== null)
		{
			const values = Array.isArray(initialValue) ? initialValue : [initialValue];
			const uniqueValues = Array.from(new Set(values.filter(v =>
				v !== null &&
				v !== undefined &&
				v !== '' &&
				(typeof v === 'string' ? v.trim() !== '' : true)
			)));
			this.selectedOptions(uniqueValues);
		}

		this.load_options();
	}

	async load_options() {
		let rawItems = [];
		let options = this.params.options || this.params.ui_fields?.options;

		// 1. Items from cache
		if (this.params.items_from_cache || this.params.ui_fields?.items_from_cache)
		{
			if (this.params.items_from_cache == 'AllCaches')
			{
				let x = Grape.cache.cache_items || {};
				rawItems = Object.keys(x);
				this.optionsText('text');
				this.optionsValue('value');
			}
			else
			{
				let cache;
				if (this.params.items_from_cache)
					cache = this.params.items_from_cache;
				else
					cache = this.params.ui_fields.items_from_cache;

				try
				{
					rawItems = await Grape.cache.get(cache);

					// Ensure proper text/value fields for cache items
					if (Array.isArray(rawItems) && rawItems.length > 0)
					{
						let first = rawItems[0];
						if (typeof first === 'string' || typeof first === 'number')
						{
							rawItems = rawItems.map(i => ({ text: String(i), value: i }));
							this.optionsText('text');
							this.optionsValue('value');
						}
						else if (typeof first === 'object')
						{
							if (first.hasOwnProperty('name') && !this.optionsText())
								this.optionsText('name');

							if (this.params.options_value || this.params.list_value_field)
							{
								if (first.hasOwnProperty('value'))
									this.optionsValue('value');
							}
							if (!this.optionsText())
							{
								let keys = Object.keys(first);
								let stringKey = keys.find(k => typeof first[k] === 'string' || typeof first[k] === 'number');
								if (stringKey)
									this.optionsText(stringKey);
							}
						}
					}
				} catch (err)
				{
					console.error('Cache error:', err);
					rawItems = [];
				}
			}
		}
		// 2. Lookup
		else if (this.params.lookup || this.params.ui_fields?.lookup)
		{
			let res = await Grape.fetches.getJSON('/api/record', {
				schema: 'grape',
				table: 'v_lookup_value',
				filter: [{ field: 'lookup_name', operand: '=', value: this.params.lookup }]
			});

			rawItems = res.records || [];
			if (!this.optionsText()) this.optionsText('name');
			if (!this.optionsValue()) this.optionsValue('lookup_value_id');
		}
		// 3. Manual items (only if no cache/lookup/fetch)
		else if (this.params.items)
		{
			if (typeof this.params.items === 'string')
			{
				try
				{
					if (this.params.items.trim().startsWith('[') && this.params.items.trim().endsWith(']'))
					{
						let content = this.params.items.trim().slice(1, -1);
						rawItems = content.split(',').map(item =>
							item.trim().replace(/^['"]|['"]$/g, '')
						);
					}
					else
					{
						try
						{
							rawItems = JSON.parse(this.params.items);
						} catch
						{
							rawItems = this.params.items.split(',').map(item => item.trim().replace(/^['"]|['"]$/g, ''));
						}
					}
				} catch (err)
				{
					console.error('Invalid manual items:', err);
					rawItems = [];
				}
			}
			else if (Array.isArray(this.params.items))
				rawItems = this.params.items;
		}
		// 4. Legacy manual options (only if no other source)
		else if (options)
		{
			if (typeof options === 'string')
			{
				try
				{
					if (options.trim().startsWith('[') && options.trim().endsWith(']'))
					{
						let content = options.trim().slice(1, -1);
						rawItems = content.split(',').map(item =>
							item.trim().replace(/^['"]|['"]$/g, '')
						);
					}
					else
					{
						try
						{
							rawItems = JSON.parse(options);
						} catch
						{
							rawItems = options.split(',').map(item => item.trim().replace(/^['"]|['"]$/g, ''));
						}
					}
				} catch (err)
				{
					console.error('Invalid manual options:', err);
					rawItems = [];
				}
			}
			else if (Array.isArray(options))
				rawItems = options;
		}

		// Normalize items array
		let items = [];
		let first = rawItems[0];

		if (typeof first === 'string' || typeof first === 'number')
		{
			items = rawItems
				.filter(i => i !== null && i !== undefined && i !== '' && (typeof i === 'string' ? i.trim() !== '' : true))
				.map(i => ({ text: String(i), value: i }));
			this.optionsText('text');
			this.optionsValue('value');
		}
		else if (first && typeof first === 'object')
		{
			items = rawItems.filter(i => i !== null && i !== undefined);
			if (!this.optionsText()) this.optionsText('name');
		}

		this.options(items);

		if (!this._updating)
		{
			const currentValue = this.$input();
			if (currentValue !== undefined && currentValue !== null)
			{
				const values = Array.isArray(currentValue) ? currentValue : [currentValue];
				const uniqueValues = Array.from(new Set(values.filter(v =>
					v !== null &&
					v !== undefined &&
					v !== '' &&
					(typeof v === 'string' ? v.trim() !== '' : true)
				)));

				if (uniqueValues.length > 0)
				{
					const matchingOptions = uniqueValues.map(val => {
						if (this.optionsValue())
						{
							return items.find(opt => {
								const optValue = opt[this.optionsValue()];
								const valValue = typeof val === 'object' ? val[this.optionsValue()] : val;
								return optValue === valValue;
							});
						}

						if (typeof val === 'object')
						{
							return items.find(opt =>
								Object.keys(opt).every(key => opt[key] === val[key])
							);
						}
						return items.find(opt => opt === val);
					}).filter(Boolean);

					if (matchingOptions.length > 0)
						this.selectedOptions(matchingOptions);
					else
						this.selectedOptions(uniqueValues);
				}
			}
		}
	}
}

export default {
	name: 'ko-field-input-multiselect-list',
	viewModel: MultiSelectListInputComponentVM,
	module_type: 'ko',
	template: template
}
